import {
  bedrockModelsExtract,
  MODELS_HELPER_TEXT,
  MODELS_PREFIX,
  withAllowedModelsHint,
} from "../models";
import { awsOidcSteps } from "../aws/oidc";
import { customAuth } from "../standardAuthPatterns";
import { defineProduct, type ListFieldFetch, productVariant } from "../types";
import { domainToRegex, widenUrlPattern } from "../urlPatternConversion";
import logo from "./logo.svg";
import { awsBedrockUsage } from "./usage";

const escapeRegex = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const familyUrlPattern = (region: string) =>
  `https://bedrock(-[a-z-]+)?\\.${escapeRegex(region)}\\.amazonaws\\.com/.*`;

const bedrockUrlPattern = ({ region, endpointUrl }: Record<string, string>) => {
  const trimmedRegion = region?.trim();
  const family = trimmedRegion
    ? familyUrlPattern(trimmedRegion)
    : "https://bedrock(-[a-z-]+)?\\..*\\.amazonaws\\.com/.*";
  const endpoint = endpointUrl?.trim();
  return endpoint ? widenUrlPattern(family, domainToRegex(endpoint)) : family;
};

export const bedrockRegion = ({ region }: Record<string, string>) =>
  region?.trim() || "us-east-1";

const bedrockTestUrl = (configuration: Record<string, string>) =>
  `https://bedrock.${bedrockRegion(configuration)}.amazonaws.com/foundation-models`;

export const bedrockRuntimeEndpoint = (configuration: Record<string, string>) =>
  configuration.endpointUrl?.trim() ||
  `https://bedrock-runtime.${bedrockRegion(configuration)}.amazonaws.com`;

const bedrockModelsFetch: ListFieldFetch = {
  label: "Fetch models from Bedrock",
  request: bedrockTestUrl,
  extract: bedrockModelsExtract,
};

const bedrockEnvVars = (
  configuration: Record<string, string>,
  { sessionToken }: { sessionToken: boolean }
): Record<string, string> => {
  const env: Record<string, string> = {
    AWS_ACCESS_KEY_ID: "{will-be-injected}",
    AWS_SECRET_ACCESS_KEY: "{will-be-injected}",
    AWS_REGION: bedrockRegion(configuration),
  };
  if (sessionToken) env.AWS_SESSION_TOKEN = "{will-be-injected}";
  const endpoint = configuration.endpointUrl?.trim();
  if (endpoint) env.AWS_ENDPOINT_URL_BEDROCK_RUNTIME = endpoint;
  return env;
};

const regionHelperText =
  "Bedrock is regional — this connector is scoped to Bedrock in the region you enter (bedrock.<region>.amazonaws.com and bedrock-runtime.<region>.amazonaws.com).";

const bedrockRegionField = {
  kind: "input",
  label: "Region",
  placeholder: "us-east-1",
  required: true,
  helperText: regionHelperText,
  target: { kind: "configuration", field: "region" },
} as const;

const vpcEndpointMarkdown =
  "If you reach Bedrock over [AWS PrivateLink](https://docs.aws.amazon.com/bedrock/latest/userguide/usingVPC.html), enter your interface VPC endpoint URL below. Requests are sent there instead of the public Bedrock runtime endpoint.\n\nLeave blank to use the public `bedrock-runtime.<region>.amazonaws.com` endpoint.";

const vpcEndpointPlaceholder =
  "https://vpce-0abc123-def456.bedrock-runtime.us-east-1.vpce.amazonaws.com";

const vpcEndpointHelperText =
  "Your PrivateLink interface endpoint for the Bedrock runtime.";

export const awsBedrock = defineProduct({
  name: "AWS Bedrock",
  tagLine: "Amazon Bedrock model inference",
  logo,
  tags: ["featured", "ai", "engineering"],
  variants: [
    productVariant({
      identifier: "awsBedrock",
      authLabel: "Access key",
      protocol: "http",
      defaultUrlPattern: bedrockUrlPattern,
      auth: (app) =>
        customAuth("awsSigV4", {
          accessKeyId: app.config.accessKeyId,
          secretAccessKey: app.secret.secretAccessKey,
          sessionToken: { from: "secret", field: "sessionToken" },
          region: app.config.region,
        }),
      steps: [
        {
          title: "Create an IAM user with Bedrock access",
          items: [
            {
              kind: "markdown",
              content:
                "Visit the [IAM Console](https://console.aws.amazon.com/iam/home#/users) and create a new user with programmatic access, or use an existing IAM user.\n\nAttach a policy granting access to Amazon Bedrock — for example `AmazonBedrockFullAccess`, or a scoped policy allowing `bedrock:InvokeModel`, `bedrock:InvokeModelWithResponseStream`, and `bedrock:ListFoundationModels`.",
            },
          ],
        },
        {
          title: "Generate access keys",
          items: [
            {
              kind: "markdown",
              content:
                "In the IAM user’s **Security credentials** tab, create a new access key. Choose **Application running outside AWS** as the use case.\n\nCopy the **Access key ID** and **Secret access key** — you’ll need them in the next step.",
            },
          ],
        },
        {
          title: "Enter your Bedrock credentials",
          items: [
            {
              kind: "input",
              label: "Access key ID",
              placeholder: "AKIA...",
              required: true,
              target: { kind: "configuration", field: "accessKeyId" },
            },
            {
              kind: "input",
              label: "Secret access key",
              placeholder: "Paste your secret access key",
              required: true,
              target: { kind: "secrets", field: "secretAccessKey" },
            },
            bedrockRegionField,
          ],
        },
        {
          title: "Use a VPC endpoint (optional)",
          items: [
            { kind: "markdown", content: vpcEndpointMarkdown },
            {
              kind: "url",
              label: "VPC endpoint URL (optional)",
              placeholder: vpcEndpointPlaceholder,
              helperText: vpcEndpointHelperText,
              target: { kind: "configuration", field: "endpointUrl" },
            },
          ],
        },
        {
          title: "Choose available models (optional)",
          items: [
            {
              kind: "list",
              label: "Available models",
              prefix: MODELS_PREFIX,
              columns: [
                {
                  field: "name",
                  placeholder: "anthropic.claude-3-5-sonnet-20241022-v2:0",
                },
              ],
              addLabel: "Add model",
              fetch: bedrockModelsFetch,
              helperText: MODELS_HELPER_TEXT,
            },
          ],
        },
      ],
      connectionTest: { protocol: "http", request: bedrockTestUrl },
      usageHint: (configuration) =>
        withAllowedModelsHint(
          "Call Amazon Bedrock with the AWS SDK or CLI as usual — list foundation models or inference profiles on the control plane (bedrock), then invoke on the runtime (bedrock-runtime). Pass a placeholder access key and secret; the proxy strips the local signature and re-signs each Bedrock request with the connected AWS credentials. Use process.env.AWS_REGION for the region, and process.env.AWS_ENDPOINT_URL_BEDROCK_RUNTIME as the endpoint when it is set. Only Bedrock calls in this region are signed by this connector — other AWS services are not.",
          configuration
        ),
      publicEnvVars: (configuration) =>
        bedrockEnvVars(configuration, { sessionToken: false }),
      usage: awsBedrockUsage(),
    }),
    productVariant({
      identifier: "awsBedrockRole",
      authLabel: "IAM role (OIDC)",
      protocol: "http",
      defaultUrlPattern: bedrockUrlPattern,
      auth: (app) =>
        customAuth("awsRole", {
          roleArn: app.config.roleArn,
          issuer: app.issuer ?? "",
        }),
      steps: [
        ...awsOidcSteps,
        {
          title: "Grant Bedrock access",
          items: [
            {
              kind: "markdown",
              content:
                "Attach a policy granting access to Amazon Bedrock — for example `AmazonBedrockFullAccess`, or a scoped policy allowing `bedrock:InvokeModel`, `bedrock:InvokeModelWithResponseStream`, and `bedrock:ListFoundationModels`.",
            },
          ],
        },
        {
          title: "Enter the Bedrock region",
          items: [bedrockRegionField],
        },
        {
          title: "Use a VPC endpoint (optional)",
          items: [
            { kind: "markdown", content: vpcEndpointMarkdown },
            {
              kind: "url",
              label: "VPC endpoint URL (optional)",
              placeholder: vpcEndpointPlaceholder,
              helperText: vpcEndpointHelperText,
              target: { kind: "configuration", field: "endpointUrl" },
            },
          ],
        },
        {
          title: "Choose available models (optional)",
          items: [
            {
              kind: "list",
              label: "Available models",
              prefix: MODELS_PREFIX,
              columns: [
                {
                  field: "name",
                  placeholder: "anthropic.claude-3-5-sonnet-20241022-v2:0",
                },
              ],
              addLabel: "Add model",
              fetch: bedrockModelsFetch,
              helperText: MODELS_HELPER_TEXT,
            },
          ],
        },
      ],
      connectionTest: { protocol: "http", request: bedrockTestUrl },
      usageHint: (configuration) =>
        withAllowedModelsHint(
          "The AWS_* env var values are placeholder strings, not real credentials — leave them in place so the AWS SDK and aws CLI pick them up and attempt to sign, but don’t branch on or print them. The proxy intercepts every outbound Bedrock call (control plane and runtime), strips the local signature, and re-signs with short-lived AssumeRoleWithWebIdentity credentials. Use process.env.AWS_REGION for the region, and process.env.AWS_ENDPOINT_URL_BEDROCK_RUNTIME as the endpoint when it is set. Only Bedrock calls in this region are signed by this connector — other AWS services are not.",
          configuration
        ),
      publicEnvVars: (configuration) =>
        bedrockEnvVars(configuration, { sessionToken: true }),
      usage: awsBedrockUsage(),
    }),
  ],
});
